import { useCallback } from 'react';

/**
 * Custom hook for handling keyboard navigation in a segmented control component.
 * Provides arrow key navigation with wrapping behavior for improved accessibility.
 *
 * @param childValues - Array of string values representing all available options in the segmented control
 * @param currentValue - The currently selected value (undefined if none selected)
 * @param itemRefs - Map of value strings to their corresponding HTML button element refs for direct DOM manipulation
 * @param disabled - Whether the entire segmented control is disabled (prevents navigation when true)
 * @returns Keyboard event handler function for the segmented control container
 */
export const useKeyboardNavigation = (
    childValues: string[],
    currentValue: string | undefined,
    itemRefs: React.MutableRefObject<Map<string, React.RefObject<HTMLButtonElement>>>,
    disabled: boolean | undefined
) => {
    return useCallback(
        (event: React.KeyboardEvent<HTMLDivElement>) => {
            if (disabled || childValues.length <= 1) return;

            // Find the current selection index to determine next navigation target
            const currentIndex = childValues.findIndex((val) => val === currentValue);
            let nextIndex: number;

            switch (event.key) {
                case 'ArrowLeft':
                case 'ArrowUp':
                    event.preventDefault();
                    nextIndex = currentIndex <= 0 ? childValues.length - 1 : currentIndex - 1;
                    break;
                case 'ArrowRight':
                case 'ArrowDown':
                    event.preventDefault();
                    nextIndex = currentIndex >= childValues.length - 1 ? 0 : currentIndex + 1;
                    break;
                default:
                    return;
            }

            // Get the target option value and its corresponding DOM reference
            const nextValue = childValues[nextIndex];
            const nextElementRef = itemRefs.current.get(nextValue);

            // Programmatically trigger selection and focus on the target element
            if (nextElementRef?.current) {
                // Click to trigger selection change (calls onChange handler)
                nextElementRef.current.click?.();
                // Focus for visual indication and continued keyboard navigation
                nextElementRef.current.focus?.();
            }
        },
        [disabled, childValues, currentValue, itemRefs]
    );
};
